import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { ArrowLeft, Plus, Trash2, Save, Send, FileText, CheckCircle2, X, Palette } from "lucide-react";

const CATEGORIES = [
  { v: "subscription", t: "Recruiter subscription" },
  { v: "boost", t: "Job boost" },
  { v: "addon", t: "Add-on / one-time" },
  { v: "enterprise", t: "Enterprise / custom" },
];

const blankPlan = {
  category: "subscription", name: "", description: "",
  price_pence: 0, currency: "gbp", billing_cycle: "monthly",
  stripe_price_id: "",
  monthly_price_pence: 0, yearly_price_pence: 0,
  stripe_price_id_monthly: "", stripe_price_id_yearly: "",
  features: [], tag: "", is_most_popular: false,
  position: 100, is_published: true, cta_label: "", cta_url: "",
  max_jobs: -1, max_applications_per_month: -1, max_team_seats: 1,
  max_boosts_per_month: -1,
  max_featured_boosts_per_month: -1, max_spotlight_boosts_per_month: -1,
  is_custom: false,
};

const blankInvoice = {
  customer_email: "", customer_name: "", company_name: "",
  line_items: [{ description: "", quantity: 1, unit_pence: 0 }],
  currency: "gbp", notes: "", discount_percent: 0,
  instalments_count: 1, instalments_interval_days: 30,
  due_at: "",
};

const inputCls = "w-full border border-[#0F0F0F] bg-transparent px-3 py-2";

export default function AdminPlans() {
  const [tab, setTab] = useState("plans");
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null);
  const [content, setContent] = useState({ headline: "", sub_headline: "", enterprise_blurb: "", faq: [] });
  const [invoiceTpl, setInvoiceTpl] = useState({ business_name: "", business_address: "", vat_number: "", footer_text: "", bank_details: "", payment_instructions: "", accent_color: "", show_logo: true, bank_name: "", account_number: "", sort_code: "", iban: "" });
  const [useDbPlans, setUseDbPlans] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [drafting, setDrafting] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Extra Fee and Reinstate States
  const [feeInvoice, setFeeInvoice] = useState(null);
  const [feeDesc, setFeeDesc] = useState("Late Fee");
  const [feeAmount, setFeeAmount] = useState("15.00");

  const loadPlans = async () => {
    const { data } = await api.get("/admin/plans");
    setPlans(data.plans || []);
  };
  const loadContent = async () => {
    const { data } = await api.get("/pricing/content");
    setContent({ ...data, faq: data.faq || [] });
  };
  const loadInvoices = async () => {
    const { data } = await api.get("/admin/invoices");
    setInvoices(data.invoices || []);
  };
  const loadTpl = async () => {
    const { data } = await api.get("/admin/invoice-template");
    setInvoiceTpl(data);
  };
  const loadUseDb = async () => {
    try { const { data } = await api.get("/admin/plans/use-db"); setUseDbPlans(!!data.use_db_plans); } catch (_e) { /* default true */ }
  };
  useEffect(() => { loadPlans(); loadContent(); loadInvoices(); loadTpl(); loadUseDb(); }, []);

  const toggleUseDbPlans = async () => {
    const next = !useDbPlans;
    if (!next && !window.confirm("Disable Custom Plans? /pricing, /billing, and Stripe checkout will use the hardcoded defaults (Free / Starter £12.99 / Growth £29.99 / Enterprise). Your saved plans stay in the DB — you can re-enable anytime.")) return;
    try {
      const { data } = await api.put("/admin/plans/use-db", { use_db_plans: next });
      setUseDbPlans(data.use_db_plans);
      setSuccess(next ? "Custom plans enabled — DB plans drive pricing." : "Custom plans disabled — scripted defaults are in effect.");
      setTimeout(() => setSuccess(""), 4000);
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const saveTpl = async () => {
    setError(""); setSuccess("");
    try {
      await api.put("/admin/invoice-template", invoiceTpl);
      setSuccess("Invoice template saved.");
      setTimeout(() => setSuccess(""), 3000);
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const previewInvoicePdf = async () => {
    // Pick the first invoice (or alert if none) and open the PDF
    const inv = invoices[0];
    if (!inv) { alert("Create an invoice first to preview the PDF."); return; }
    const res = await api.get(`/invoices/${inv.invoice_id}/pdf`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    window.open(url, "_blank");
  };

  const savePlan = async () => {
    if (!editing) return;
    setError(""); setSuccess("");
    try {
      const payload = { ...editing, price_pence: Number(editing.price_pence) || 0, position: Number(editing.position) || 100 };
      if (editing.plan_id) {
        await api.put(`/admin/plans/${editing.plan_id}`, payload);
      } else {
        await api.post("/admin/plans", payload);
      }
      setEditing(null);
      await loadPlans();
      setSuccess("Plan saved.");
      setTimeout(() => setSuccess(""), 3000);
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const deletePlan = async (id) => {
    if (!window.confirm("Delete this plan? It will no longer appear on /pricing.")) return;
    await api.delete(`/admin/plans/${id}`);
    await loadPlans();
  };

  const saveContent = async () => {
    await api.put("/admin/pricing/content", content);
    setSuccess("Pricing page content saved.");
    setTimeout(() => setSuccess(""), 3000);
  };

  const createInvoice = async () => {
    setError("");
    try {
      const payload = {
        customer_email: drafting.customer_email,
        customer_name: drafting.customer_name,
        company_name: drafting.company_name,
        currency: drafting.currency,
        notes: drafting.notes,
        discount_percent: Number(drafting.discount_percent) || 0,
        instalments_count: Number(drafting.instalments_count) || 1,
        instalments_interval_days: Number(drafting.instalments_interval_days) || 30,
        due_at: drafting.due_at ? new Date(drafting.due_at).toISOString() : null,
        line_items: drafting.line_items.map((li) => ({
          description: li.description,
          quantity: Number(li.quantity) || 1,
          unit_pence: Number(li.unit_pence) || 0,
        })),
      };
      if (drafting._editing_id) {
        await api.put(`/admin/invoices/${drafting._editing_id}`, payload);
      } else {
        await api.post("/admin/invoices", payload);
      }
      setDrafting(null);
      await loadInvoices();
      setSuccess(drafting._editing_id ? "Invoice updated — click 'Resend' to push fresh payment links." : "Invoice created — click 'Send' to email it.");
      setTimeout(() => setSuccess(""), 4000);
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const sendInvoice = async (id) => {
    await api.post(`/admin/invoices/${id}/send`);
    await loadInvoices();
    setSuccess("Invoice emailed.");
    setTimeout(() => setSuccess(""), 3000);
  };

  const resendInvoice = async (id) => {
    if (!window.confirm("Regenerate fresh Stripe Payment Links and re-email the customer?")) return;
    await api.post(`/admin/invoices/${id}/resend`);
    await loadInvoices();
    setSuccess("Resent with fresh payment links.");
    setTimeout(() => setSuccess(""), 3000);
  };

  const remindInvoice = async (id) => {
    await api.post(`/admin/invoices/${id}/remind`);
    setSuccess("Reminder sent.");
    setTimeout(() => setSuccess(""), 3000);
  };

  const cancelInvoice = async (id) => {
    if (!window.confirm("Void this invoice? It will show as cancelled to the customer.")) return;
    await api.post(`/admin/invoices/${id}/cancel`);
    await loadInvoices();
    setSuccess("Invoice voided.");
    setTimeout(() => setSuccess(""), 3000);
  };

  const handleReinstateInvoice = async (id) => {
    if (!window.confirm("Reinstate this voided invoice? It will revert back to draft status.")) return;
    setError(""); setSuccess("");
    try {
      await api.post(`/admin/invoices/${id}/reinstate`);
      await loadInvoices();
      setSuccess("Invoice successfully reinstated to draft!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const handleSaveExtraFee = async () => {
    if (!feeDesc.trim() || !feeAmount) {
      alert("Please fill in both description and fee amount.");
      return;
    }
    setError(""); setSuccess("");
    try {
      const amountPence = Math.round(parseFloat(feeAmount) * 100);
      await api.post(`/admin/invoices/${feeInvoice.invoice_id}/extra-fee`, {
        description: feeDesc,
        amount_pence: amountPence,
      });
      setFeeInvoice(null);
      await loadInvoices();
      setSuccess("Extra fee successfully added to invoice and split/allocated to next instalment!");
      setTimeout(() => setSuccess(""), 4000);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const editInvoice = (inv) => {
    setDrafting({
      ...inv,
      _editing_id: inv.invoice_id,
      instalments_count: inv.instalments_count || 1,
      instalments_interval_days: 30,
      due_at: inv.due_at ? inv.due_at.slice(0, 10) : "",
    });
  };

  const markPaid = async (invId, idx) => {
    await api.post(`/admin/invoices/${invId}/instalment/${idx}/mark-paid`);
    await loadInvoices();
  };

  const markInvoicePaid = async (invId) => {
    if (!window.confirm("Mark this entire invoice as paid?")) return;
    try {
      await api.post(`/admin/invoices/${invId}/mark-paid`);
      await loadInvoices();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const downloadInvoicePdf = async (id) => {
    try {
      const res = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10">
      <Link to="/admin" data-testid="back-to-admin" className="inline-flex items-center gap-1 text-sm text-[#5C5C5C] hover:text-[#0F0F0F]"><ArrowLeft className="w-3 h-3" /> Back to admin</Link>
      <h1 className="font-display font-black text-4xl mt-3">Plans, pricing & invoices.</h1>
      <p className="text-sm text-[#5C5C5C] mt-1">Owner-only. Everything you publish here drives the public /pricing page and customer invoices.</p>

      <div className="mt-6 flex gap-1 border-b border-[#E2E0D8]">
        {[["plans", "Plans"], ["content", "Pricing page content"], ["invoices", "Invoices"], ["template", "Invoice template"]].map(([v, t]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`plans-tab-${v}`} className={`px-4 py-2 text-sm font-display font-bold uppercase tracking-widest ${tab === v ? "border-b-2 border-[var(--brand-primary)] text-[#0F0F0F]" : "text-[#5C5C5C] hover:text-[#0F0F0F]"}`}>
            {t}
          </button>
        ))}
      </div>

      {error && <div data-testid="plans-error" className="mt-4 text-sm text-[var(--brand-primary)]">{error}</div>}
      {success && <div data-testid="plans-success" className="mt-4 text-sm text-green-700">{success}</div>}

      {tab === "plans" && (
        <div className="mt-6">
          <div className="flex gap-2 flex-wrap mb-4 items-center">
            <button onClick={() => setEditing({ ...blankPlan, plan_id: null })} data-testid="add-plan-btn" className="btn-terra"><Plus className="w-4 h-4" /> New plan</button>
            <button
              onClick={async () => {
                if (!window.confirm("Restore the original default plans (Free, Starter, Growth, Enterprise, Single boost, Bulk boost)?\n\nWARNING: this REPLACES all current plans.")) return;
                try {
                  const { data } = await api.post("/admin/plans/restore-defaults");
                  await loadPlans();
                  setSuccess(`Restored ${data.restored} default plans.`);
                  setTimeout(() => setSuccess(""), 4000);
                } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
              }}
              data-testid="restore-defaults-btn"
              className="btn-ghost text-sm"
            >
              Restore defaults
            </button>
            <button
              onClick={toggleUseDbPlans}
              data-testid="toggle-use-db-plans"
              className={`text-sm font-display font-bold uppercase tracking-widest px-4 py-2 transition ${useDbPlans ? "bg-[#0F0F0F] text-[#FDFCF8] hover:bg-[var(--brand-primary)]" : "bg-[var(--brand-primary)] text-white hover:bg-[#B83A28]"}`}
              title={useDbPlans ? "Active: Custom plans drive /pricing + Stripe" : "Active: Hardcoded defaults in effect"}
            >
              {useDbPlans ? "Disable Custom Plans" : "Enable Custom Plans"}
            </button>
            <span className={`text-[10px] uppercase tracking-widest font-semibold ${useDbPlans ? "text-green-700" : "text-[var(--brand-primary)]"}`} data-testid="use-db-status">
              {useDbPlans ? "Custom plans ON" : "Scripted defaults ON"}
            </span>
          </div>
          <div className="grid gap-3">
            {plans.length === 0 && <div className="text-sm text-[#5C5C5C]">No plans yet. Click {'"New plan"'} to publish your first one.</div>}
            {plans.map((p) => (
              <div key={p.plan_id} data-testid={`plan-row-${p.plan_id}`} className="border border-[#E2E0D8] p-4 flex items-center justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-widest font-semibold border border-[#0F0F0F] px-1.5 py-0.5">{p.category}</span>
                    {p.is_most_popular && <span className="text-[10px] uppercase tracking-widest font-semibold bg-[var(--brand-primary)] text-white px-1.5 py-0.5">Most popular</span>}
                    {p.tag && <span className="text-[10px] uppercase tracking-widest font-semibold bg-[#0F0F0F] text-[#FDFCF8] px-1.5 py-0.5">{p.tag}</span>}
                    {!p.is_published && <span className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C5C]">Hidden</span>}
                  </div>
                  <div className="font-display font-black text-xl mt-1">{p.name} <span className="text-sm text-[#5C5C5C] font-normal">— £{(p.price_pence/100).toFixed(2)} / {p.billing_cycle}</span></div>
                  <div className="text-xs text-[#5C5C5C] mt-0.5">{(p.features || []).length} feature{p.features.length === 1 ? "" : "s"} · stripe_price_id: {p.stripe_price_id || "—"}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setEditing({ ...p })} data-testid={`edit-plan-${p.plan_id}`} className="btn-ghost text-sm">Edit</button>
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Reset "${p.name}" to its default values? Pricing, features, tag, and quotas will revert. Other plans untouched.`)) return;
                      try {
                        await api.post(`/admin/plans/${p.plan_id}/reset`);
                        await loadPlans();
                        setSuccess(`"${p.name}" restored to defaults.`);
                        setTimeout(() => setSuccess(""), 3000);
                      } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
                    }}
                    data-testid={`reset-plan-${p.plan_id}`}
                    className="btn-ghost text-sm"
                    title="Restore this single plan's defaults"
                  >
                    Reset
                  </button>
                  <button onClick={() => deletePlan(p.plan_id)} data-testid={`delete-plan-${p.plan_id}`} className="btn-ghost text-sm text-[var(--brand-primary)]"><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "content" && (
        <div className="mt-6 max-w-2xl space-y-3" data-testid="content-editor">
          <div><label className="label-eyebrow block mb-1.5">Headline</label><input data-testid="content-headline" className={inputCls} value={content.headline || ""} onChange={(e) => setContent({ ...content, headline: e.target.value })} /></div>
          <div><label className="label-eyebrow block mb-1.5">Sub-headline</label><input data-testid="content-sub" className={inputCls} value={content.sub_headline || ""} onChange={(e) => setContent({ ...content, sub_headline: e.target.value })} /></div>
          <div><label className="label-eyebrow block mb-1.5">Enterprise blurb</label><textarea rows={3} data-testid="content-blurb" className={inputCls} value={content.enterprise_blurb || ""} onChange={(e) => setContent({ ...content, enterprise_blurb: e.target.value })} /></div>
          <div>
            <div className="flex items-center justify-between mb-1.5"><label className="label-eyebrow">FAQ</label><button onClick={() => setContent({ ...content, faq: [...(content.faq || []), { q: "", a: "" }] })} data-testid="add-faq" className="text-xs underline">+ Add FAQ</button></div>
            {(content.faq || []).map((f, i) => (
              <div key={i} className="border border-[#E2E0D8] p-3 mb-2 space-y-2">
                <input placeholder="Question" data-testid={`faq-q-${i}`} className={inputCls} value={f.q} onChange={(e) => { const next = [...content.faq]; next[i] = { ...next[i], q: e.target.value }; setContent({ ...content, faq: next }); }} />
                <textarea placeholder="Answer" rows={2} data-testid={`faq-a-${i}`} className={inputCls} value={f.a} onChange={(e) => { const next = [...content.faq]; next[i] = { ...next[i], a: e.target.value }; setContent({ ...content, faq: next }); }} />
                <button onClick={() => setContent({ ...content, faq: content.faq.filter((_, j) => j !== i) })} className="text-xs text-[var(--brand-primary)] underline">remove</button>
              </div>
            ))}
          </div>
          <button onClick={saveContent} data-testid="save-content" className="btn-terra"><Save className="w-4 h-4" /> Save pricing page</button>
        </div>
      )}

      {tab === "invoices" && (
        <div className="mt-6 space-y-4">
          <button onClick={() => setDrafting({ ...blankInvoice })} data-testid="new-invoice-btn" className="btn-terra"><Plus className="w-4 h-4" /> New invoice</button>
          {invoices.length === 0 && <div className="text-sm text-[#5C5C5C]">No invoices yet.</div>}
          {invoices.map((inv) => (
            <div key={inv.invoice_id} data-testid={`invoice-row-${inv.invoice_id}`} className="border border-[#E2E0D8] p-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-widest font-semibold border border-[#0F0F0F] px-1.5 py-0.5">{inv.status}</span>
                    <span className="font-mono text-xs">{inv.invoice_number}</span>
                  </div>
                  <div className="font-display font-bold text-lg mt-1">{inv.customer_name || inv.customer_email} — £{(inv.total_pence/100).toFixed(2)}{inv.discount_percent > 0 ? ` (${inv.discount_percent}% off)` : ""}</div>
                  <div className="text-xs text-[#5C5C5C]">{inv.customer_email} · {inv.instalments_count} instalment{inv.instalments_count === 1 ? "" : "s"}</div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={() => downloadInvoicePdf(inv.invoice_id)} data-testid={`download-invoice-${inv.invoice_id}`} className="btn-ghost text-sm">Download PDF</button>
                  {inv.status !== "paid" && inv.status !== "void" && (
                    <button onClick={() => markInvoicePaid(inv.invoice_id)} data-testid={`mark-paid-entire-${inv.invoice_id}`} className="btn-ghost text-sm text-green-700">Mark Paid</button>
                  )}
                  {inv.status === "draft" && <button onClick={() => sendInvoice(inv.invoice_id)} data-testid={`send-invoice-${inv.invoice_id}`} className="btn-ghost text-sm"><Send className="w-3 h-3" /> Send</button>}
                  {inv.status !== "paid" && inv.status !== "void" && inv.status !== "draft" && (
                    <button onClick={() => resendInvoice(inv.invoice_id)} data-testid={`resend-invoice-${inv.invoice_id}`} className="btn-ghost text-sm"><Send className="w-3 h-3" /> Resend</button>
                  )}
                  {inv.status !== "paid" && inv.status !== "void" && inv.status !== "draft" && (
                    <button onClick={() => remindInvoice(inv.invoice_id)} data-testid={`remind-invoice-${inv.invoice_id}`} className="btn-ghost text-sm">Remind</button>
                  )}
                  {inv.status !== "paid" && inv.status !== "void" && (
                    <button onClick={() => editInvoice(inv)} data-testid={`edit-invoice-${inv.invoice_id}`} className="btn-ghost text-sm">Edit</button>
                  )}
                  {inv.status !== "paid" && inv.status !== "void" && (
                    <button onClick={() => cancelInvoice(inv.invoice_id)} data-testid={`cancel-invoice-${inv.invoice_id}`} className="btn-ghost text-sm text-[var(--brand-primary)]">Void</button>
                  )}
                  {inv.status === "void" && (
                    <button onClick={() => handleReinstateInvoice(inv.invoice_id)} data-testid={`reinstate-invoice-${inv.invoice_id}`} className="btn-ghost text-sm text-green-700">Reinstate</button>
                  )}
                  {inv.status !== "void" && inv.status !== "paid" && (
                    <button onClick={() => { setFeeInvoice(inv); setFeeDesc("Late Payment Fee"); setFeeAmount("15.00"); }} data-testid={`add-fee-invoice-${inv.invoice_id}`} className="btn-ghost text-sm text-amber-700">+ Extra/Late Fee</button>
                  )}
                </div>
              </div>
              {(inv.instalments || []).length >= 1 && (
                <div className="mt-3 grid gap-1.5 text-xs">
                  {inv.instalments.map((ins) => (
                    <div key={ins.index} className="flex items-center justify-between gap-2 border-b border-[#E2E0D8] py-1">
                      <span>#{ins.index} · £{(ins.amount_pence/100).toFixed(2)} · due {ins.due_at?.slice(0,10)}</span>
                      <span className="flex items-center gap-2">
                        <span className={`uppercase tracking-widest text-[10px] font-semibold ${ins.status === "paid" ? "text-green-700" : "text-[#5C5C5C]"}`}>{ins.status}</span>
                        {ins.status !== "paid" && <button onClick={() => markPaid(inv.invoice_id, ins.index)} data-testid={`mark-paid-${inv.invoice_id}-${ins.index}`} className="text-[10px] underline">Mark paid</button>}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "template" && (
        <div className="mt-6 max-w-2xl space-y-3" data-testid="invoice-template-editor">
          <p className="text-sm text-[#5C5C5C]">Customise how every PDF invoice looks. Changes apply to all downloaded + emailed invoices.</p>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label-eyebrow block mb-1">Business name</label><input data-testid="tpl-business-name" className={inputCls} value={invoiceTpl.business_name || ""} onChange={(e) => setInvoiceTpl({ ...invoiceTpl, business_name: e.target.value })} placeholder="Ocean Ash Ltd" /></div>
            <div><label className="label-eyebrow block mb-1">VAT number</label><input data-testid="tpl-vat" className={inputCls} value={invoiceTpl.vat_number || ""} onChange={(e) => setInvoiceTpl({ ...invoiceTpl, vat_number: e.target.value })} placeholder="GB123456789" /></div>
          </div>
          <div><label className="label-eyebrow block mb-1">Business address (multi-line)</label><textarea data-testid="tpl-address" rows={3} className={inputCls} value={invoiceTpl.business_address || ""} onChange={(e) => setInvoiceTpl({ ...invoiceTpl, business_address: e.target.value })} placeholder={"123 Quay Street\nManchester M3 4AB\nUnited Kingdom"} /></div>
          <div><label className="label-eyebrow block mb-1">Payment instructions</label><textarea data-testid="tpl-instructions" rows={2} className={inputCls} value={invoiceTpl.payment_instructions || ""} onChange={(e) => setInvoiceTpl({ ...invoiceTpl, payment_instructions: e.target.value })} placeholder="Payable by BACS within 30 days. Reference the invoice number." /></div>
          <div><label className="label-eyebrow block mb-1">Bank details (multi-line)</label><textarea data-testid="tpl-bank" rows={4} className={inputCls} value={invoiceTpl.bank_details || ""} onChange={(e) => setInvoiceTpl({ ...invoiceTpl, bank_details: e.target.value })} placeholder={"Account: Ocean Ash Ltd\nSort: 12-34-56\nAccount: 87654321\nIBAN: GB..."} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label-eyebrow block mb-1">Bank Name</label><input data-testid="tpl-bank-name" className={inputCls} value={invoiceTpl.bank_name || ""} onChange={(e) => setInvoiceTpl({ ...invoiceTpl, bank_name: e.target.value })} placeholder="Barclays Bank" /></div>
            <div><label className="label-eyebrow block mb-1">Account Number</label><input data-testid="tpl-account-number" className={inputCls} value={invoiceTpl.account_number || ""} onChange={(e) => setInvoiceTpl({ ...invoiceTpl, account_number: e.target.value })} placeholder="12345678" /></div>
            <div><label className="label-eyebrow block mb-1">Sort Code</label><input data-testid="tpl-sort-code" className={inputCls} value={invoiceTpl.sort_code || ""} onChange={(e) => setInvoiceTpl({ ...invoiceTpl, sort_code: e.target.value })} placeholder="20-30-40" /></div>
            <div><label className="label-eyebrow block mb-1">IBAN</label><input data-testid="tpl-iban" className={inputCls} value={invoiceTpl.iban || ""} onChange={(e) => setInvoiceTpl({ ...invoiceTpl, iban: e.target.value })} placeholder="GB..." /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label-eyebrow block mb-1">Footer text</label><input data-testid="tpl-footer" className={inputCls} value={invoiceTpl.footer_text || ""} onChange={(e) => setInvoiceTpl({ ...invoiceTpl, footer_text: e.target.value })} placeholder="Thank you for your business" /></div>
            <div><label className="label-eyebrow block mb-1">Accent colour (hex, optional)</label><input data-testid="tpl-accent" className={inputCls} value={invoiceTpl.accent_color || ""} onChange={(e) => setInvoiceTpl({ ...invoiceTpl, accent_color: e.target.value })} placeholder="#D94833" /></div>
          </div>
          <div className="flex gap-2 pt-2">
            <button onClick={saveTpl} data-testid="save-template" className="btn-terra"><Save className="w-4 h-4" /> Save template</button>
            <button onClick={previewInvoicePdf} data-testid="preview-template" className="btn-ghost text-sm"><FileText className="w-4 h-4" /> Preview latest invoice PDF</button>
          </div>
        </div>
      )}


      {/* Plan editor modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/70 z-50 grid place-items-center px-4">
          <div className="bg-[#FDFCF8] border border-[#0F0F0F] max-w-2xl w-full p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between"><h3 className="font-display font-black text-2xl">{editing.plan_id ? "Edit plan" : "New plan"}</h3><button onClick={() => setEditing(null)}><X className="w-4 h-4" /></button></div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div><label className="label-eyebrow block mb-1">Category</label><select data-testid="plan-category" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className={inputCls}>{CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.t}</option>)}</select></div>
              <div><label className="label-eyebrow block mb-1">Name</label><input data-testid="plan-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className={inputCls} /></div>
              <div className="col-span-2"><label className="label-eyebrow block mb-1">Description</label><textarea data-testid="plan-desc" rows={2} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className={inputCls} /></div>
              <div><label className="label-eyebrow block mb-1">Price (pence)</label><input data-testid="plan-price" type="number" value={editing.price_pence} onChange={(e) => setEditing({ ...editing, price_pence: e.target.value })} className={inputCls} /></div>
              <div><label className="label-eyebrow block mb-1">Cycle</label><select data-testid="plan-cycle" value={editing.billing_cycle} onChange={(e) => setEditing({ ...editing, billing_cycle: e.target.value })} className={inputCls}>{["monthly","yearly","one_time"].map((o) => <option key={o}>{o}</option>)}</select></div>
              <div><label className="label-eyebrow block mb-1">Stripe price id</label><input data-testid="plan-stripe" value={editing.stripe_price_id || ""} onChange={(e) => setEditing({ ...editing, stripe_price_id: e.target.value })} className={inputCls} placeholder="price_xxx" /></div>
              <div><label className="label-eyebrow block mb-1">Position (sort)</label><input data-testid="plan-position" type="number" value={editing.position} onChange={(e) => setEditing({ ...editing, position: e.target.value })} className={inputCls} /></div>

              {editing.category === "subscription" && (
                <div className="col-span-2 border-t border-[#E2E0D8] pt-3">
                  <div className="label-eyebrow mb-2">Dual-cycle pricing (optional — overrides single price above)</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-xs block mb-1">Monthly price (pence)</label><input data-testid="plan-monthly-pence" type="number" value={editing.monthly_price_pence ?? 0} onChange={(e) => setEditing({ ...editing, monthly_price_pence: parseInt(e.target.value || "0", 10) })} className={inputCls} /></div>
                    <div><label className="text-xs block mb-1">Yearly price (pence)</label><input data-testid="plan-yearly-pence" type="number" value={editing.yearly_price_pence ?? 0} onChange={(e) => setEditing({ ...editing, yearly_price_pence: parseInt(e.target.value || "0", 10) })} className={inputCls} /></div>
                    <div><label className="text-xs block mb-1">Stripe price id (monthly)</label><input data-testid="plan-stripe-monthly" value={editing.stripe_price_id_monthly || ""} onChange={(e) => setEditing({ ...editing, stripe_price_id_monthly: e.target.value })} className={inputCls} placeholder="price_xxx" /></div>
                    <div><label className="text-xs block mb-1">Stripe price id (yearly)</label><input data-testid="plan-stripe-yearly" value={editing.stripe_price_id_yearly || ""} onChange={(e) => setEditing({ ...editing, stripe_price_id_yearly: e.target.value })} className={inputCls} placeholder="price_xxx" /></div>
                  </div>
                </div>
              )}

              {editing.category === "boost" && (
                <div className="col-span-2">
                  <label className="label-eyebrow block mb-1">Boost type</label>
                  <select data-testid="plan-boost-type" value={editing.boost_type || ""} onChange={(e) => setEditing({ ...editing, boost_type: e.target.value })} className={inputCls}>
                    <option value="">— select —</option>
                    <option value="single">Single (one specific job)</option>
                    <option value="bulk">Bulk (all your jobs)</option>
                  </select>
                </div>
              )}
              <div className="col-span-2"><label className="label-eyebrow block mb-1">Tag (e.g. {'"Free During Beta"'})</label><input data-testid="plan-tag" value={editing.tag || ""} onChange={(e) => setEditing({ ...editing, tag: e.target.value })} className={inputCls} /></div>
              <div className="col-span-2">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="label-eyebrow">Features ({(editing.features || []).length})</label>
                  <button
                    type="button"
                    onClick={() => setEditing({ ...editing, features: [...(editing.features || []), ""] })}
                    data-testid="plan-add-feature"
                    className="text-xs underline"
                  >
                    + Add feature
                  </button>
                </div>
                <div className="space-y-2">
                  {(editing.features || []).map((f, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <span className="text-xs text-[#5C5C5C] w-5">{i + 1}.</span>
                      <input
                        data-testid={`plan-feature-${i}`}
                        value={f}
                        onChange={(e) => {
                          const next = [...editing.features]; next[i] = e.target.value;
                          setEditing({ ...editing, features: next });
                        }}
                        placeholder="e.g. Unlimited active jobs"
                        className={`${inputCls} flex-1`}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (i === 0) return;
                          const next = [...editing.features];
                          [next[i - 1], next[i]] = [next[i], next[i - 1]];
                          setEditing({ ...editing, features: next });
                        }}
                        disabled={i === 0}
                        className="text-xs px-1.5 py-1 border border-[#E2E0D8] hover:border-[#0F0F0F] disabled:opacity-30"
                        title="Move up"
                      >↑</button>
                      <button
                        type="button"
                        onClick={() => setEditing({ ...editing, features: editing.features.filter((_, j) => j !== i) })}
                        data-testid={`plan-feature-remove-${i}`}
                        className="text-xs px-1.5 py-1 border border-[#E2E0D8] hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)]"
                        title="Remove"
                      >✕</button>
                    </div>
                  ))}
                  {(editing.features || []).length === 0 && (
                    <div className="text-xs text-[#5C5C5C] italic">No features yet. Click {'"Add feature"'} above.</div>
                  )}
                </div>
              </div>
              <div className="col-span-2 border-t border-[#E2E0D8] pt-3">
                <div className="label-eyebrow mb-2">Quotas · use -1 for unlimited</div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs block mb-1">Max active jobs</label><input data-testid="plan-max-jobs" type="number" value={editing.max_jobs ?? -1} onChange={(e) => setEditing({ ...editing, max_jobs: parseInt(e.target.value || "-1", 10) })} className={inputCls} /></div>
                  <div><label className="text-xs block mb-1">Max applications / month</label><input data-testid="plan-max-apps" type="number" value={editing.max_applications_per_month ?? -1} onChange={(e) => setEditing({ ...editing, max_applications_per_month: parseInt(e.target.value || "-1", 10) })} className={inputCls} /></div>
                  <div><label className="text-xs block mb-1">Team seats</label><input data-testid="plan-max-seats" type="number" value={editing.max_team_seats ?? 1} onChange={(e) => setEditing({ ...editing, max_team_seats: parseInt(e.target.value || "1", 10) })} className={inputCls} /></div>
                  <div><label className="text-xs block mb-1">Featured boosts / month</label><input data-testid="plan-max-featured" type="number" value={editing.max_featured_boosts_per_month ?? -1} onChange={(e) => setEditing({ ...editing, max_featured_boosts_per_month: parseInt(e.target.value || "-1", 10) })} className={inputCls} /></div>
                  <div><label className="text-xs block mb-1">Spotlight boosts / month</label><input data-testid="plan-max-spotlight" type="number" value={editing.max_spotlight_boosts_per_month ?? -1} onChange={(e) => setEditing({ ...editing, max_spotlight_boosts_per_month: parseInt(e.target.value || "-1", 10) })} className={inputCls} /></div>
                </div>
              </div>
              
              <div className="col-span-2 border-t border-[#E2E0D8] pt-3 bg-amber-50/20 p-3 border border-amber-600/10">
                <div className="label-eyebrow mb-2 flex items-center gap-1.5 text-amber-900"><Palette className="w-3.5 h-3.5" /> Include Features (Feature Flags)</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-[#0F0F0F]">
                  {[
                    { key: "custom_email_templates", label: "Custom Email Templates" },
                    { key: "custom_careers_branding", label: "Custom Careers Page Branding" },
                    { key: "bulk_actions", label: "Bulk Applicant Actions (Enterprise)" },
                    { key: "whitelabel", label: "White-label mode (hide platform Nav/Footer)" },
                    { key: "custom_pipeline", label: "Custom Pipeline Stages (Growth+)" },
                    { key: "company_tracking", label: "Company progress tracking (Enterprise+)" }
                  ].map((flag) => {
                    const currentFlags = editing.feature_flags || {};
                    const isChecked = !!currentFlags[flag.key];
                    return (
                      <label key={flag.key} className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                          data-testid={`plan-flag-${flag.key}`}
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            const nextFlags = { ...currentFlags, [flag.key]: e.target.checked };
                            setEditing({ ...editing, feature_flags: nextFlags });
                          }}
                          className="accent-amber-600"
                        />
                        <span>{flag.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <label className="inline-flex items-center gap-2 text-sm"><input data-testid="plan-popular" type="checkbox" checked={!!editing.is_most_popular} onChange={(e) => setEditing({ ...editing, is_most_popular: e.target.checked })} /> Highlight as {'"Most Popular"'}</label>
              <label className="inline-flex items-center gap-2 text-sm"><input data-testid="plan-published" type="checkbox" checked={!!editing.is_published} onChange={(e) => setEditing({ ...editing, is_published: e.target.checked })} /> Published</label>
              <label className="inline-flex items-center gap-2 text-sm bg-amber-50 border border-amber-500/20 px-3 py-1.5 rounded-none font-semibold text-amber-900 cursor-pointer">
                <input 
                  data-testid="plan-custom-contract" 
                  type="checkbox" 
                  checked={!!editing.is_custom} 
                  onChange={(e) => setEditing({ ...editing, is_custom: e.target.checked })} 
                  className="accent-amber-600"
                /> 
                <span>Enable Contract option for this plan (Custom Contract / No scripted defaults restriction)</span>
              </label>
              <div><label className="label-eyebrow block mb-1">CTA label</label><input data-testid="plan-cta-label" value={editing.cta_label || ""} onChange={(e) => setEditing({ ...editing, cta_label: e.target.value })} className={inputCls} placeholder="Contact sales" /></div>
              <div><label className="label-eyebrow block mb-1">CTA URL</label><input data-testid="plan-cta-url" value={editing.cta_url || ""} onChange={(e) => setEditing({ ...editing, cta_url: e.target.value })} className={inputCls} placeholder="/contact-sales" /></div>
            </div>
            <button onClick={savePlan} data-testid="save-plan" className="btn-terra w-full mt-4"><Save className="w-4 h-4" /> Save plan</button>
          </div>
        </div>
      )}

      {/* Invoice draft modal */}
      {drafting && (
        <div className="fixed inset-0 bg-black/70 z-50 grid place-items-center px-4">
          <div className="bg-[#FDFCF8] border border-[#0F0F0F] max-w-2xl w-full p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between"><h3 className="font-display font-black text-2xl"><FileText className="inline w-5 h-5 mr-2" /> {drafting._editing_id ? "Edit invoice" : "New invoice"}</h3><button onClick={() => setDrafting(null)}><X className="w-4 h-4" /></button></div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div><label className="label-eyebrow block mb-1">Customer email *</label><input required data-testid="invoice-email" type="email" value={drafting.customer_email} onChange={(e) => setDrafting({ ...drafting, customer_email: e.target.value })} className={inputCls} /></div>
              <div><label className="label-eyebrow block mb-1">Customer name</label><input data-testid="invoice-name" value={drafting.customer_name} onChange={(e) => setDrafting({ ...drafting, customer_name: e.target.value })} className={inputCls} /></div>
              <div className="col-span-2"><label className="label-eyebrow block mb-1">Company</label><input data-testid="invoice-company" value={drafting.company_name} onChange={(e) => setDrafting({ ...drafting, company_name: e.target.value })} className={inputCls} /></div>
            </div>
            <div className="mt-4">
              <div className="flex items-center justify-between"><label className="label-eyebrow">Line items</label><button onClick={() => setDrafting({ ...drafting, line_items: [...drafting.line_items, { description: "", quantity: 1, unit_pence: 0 }] })} className="text-xs underline" data-testid="add-line">+ Add line</button></div>
              {drafting.line_items.map((li, i) => (
                <div key={i} className="grid grid-cols-[1fr_80px_120px_30px] gap-2 mt-2 items-center">
                  <input data-testid={`invoice-line-desc-${i}`} placeholder="Description" value={li.description} onChange={(e) => { const next = [...drafting.line_items]; next[i] = { ...next[i], description: e.target.value }; setDrafting({ ...drafting, line_items: next }); }} className={inputCls} />
                  <input data-testid={`invoice-line-qty-${i}`} type="number" placeholder="Qty" value={li.quantity} onChange={(e) => { const next = [...drafting.line_items]; next[i] = { ...next[i], quantity: e.target.value }; setDrafting({ ...drafting, line_items: next }); }} className={inputCls} />
                  <input data-testid={`invoice-line-unit-${i}`} type="number" placeholder="Pence" value={li.unit_pence} onChange={(e) => { const next = [...drafting.line_items]; next[i] = { ...next[i], unit_pence: e.target.value }; setDrafting({ ...drafting, line_items: next }); }} className={inputCls} />
                  <button onClick={() => setDrafting({ ...drafting, line_items: drafting.line_items.filter((_, j) => j !== i) })} className="text-[var(--brand-primary)]"><Trash2 className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div><label className="label-eyebrow block mb-1">Instalments</label><input data-testid="invoice-installments" type="number" min={1} value={drafting.instalments_count} onChange={(e) => setDrafting({ ...drafting, instalments_count: e.target.value })} className={inputCls} /></div>
              <div><label className="label-eyebrow block mb-1">Discount %</label><input data-testid="invoice-discount" type="number" min={0} max={100} value={drafting.discount_percent} onChange={(e) => setDrafting({ ...drafting, discount_percent: e.target.value })} className={inputCls} /></div>
              <div><label className="label-eyebrow block mb-1">Interval (days)</label><input data-testid="invoice-interval" type="number" value={drafting.instalments_interval_days} onChange={(e) => setDrafting({ ...drafting, instalments_interval_days: e.target.value })} className={inputCls} /></div>
              <div className="col-span-2"><label className="label-eyebrow block mb-1">Invoice Due Date (Customised)</label><input data-testid="invoice-due-at" type="date" value={drafting.due_at || ""} onChange={(e) => setDrafting({ ...drafting, due_at: e.target.value })} className={inputCls} /></div>
              <div className="col-span-2"><label className="label-eyebrow block mb-1">Notes</label><textarea data-testid="invoice-notes" rows={2} value={drafting.notes} onChange={(e) => setDrafting({ ...drafting, notes: e.target.value })} className={inputCls} /></div>
            </div>
            <button onClick={createInvoice} data-testid="save-invoice" disabled={!drafting.customer_email} className="btn-terra w-full mt-4 disabled:opacity-40"><CheckCircle2 className="w-4 h-4" /> {drafting._editing_id ? "Save changes" : "Create invoice"}</button>
          </div>
        </div>
      )}

      {/* Add Extra Fee Modal */}
      {feeInvoice && (
        <div className="fixed inset-0 bg-black/70 z-50 grid place-items-center px-4">
          <div className="bg-[#FDFCF8] border border-[#0F0F0F] max-w-md w-full p-6 text-left">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-black text-xl">➕ Add Extra Fee (e.g., Late Fee)</h3>
              <button onClick={() => setFeeInvoice(null)}><X className="w-4 h-4" /></button>
            </div>
            <p className="text-xs text-[#5C5C5C] mt-1.5">
              This will add a new fee line item to invoice <strong>{feeInvoice.invoice_number}</strong> and allocate the amount to its next unpaid instalment.
            </p>
            <div className="mt-4 space-y-4">
              <div>
                <label className="label-eyebrow block mb-1">Fee Description</label>
                <input
                  data-testid="fee-description"
                  type="text"
                  value={feeDesc}
                  onChange={(e) => setFeeDesc(e.target.value)}
                  className={inputCls}
                  placeholder="e.g., Late Payment Fee"
                />
              </div>
              <div>
                <label className="label-eyebrow block mb-1">Fee Amount (GBP)</label>
                <input
                  data-testid="fee-amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={feeAmount}
                  onChange={(e) => setFeeAmount(e.target.value)}
                  className={inputCls}
                  placeholder="e.g., 15.00"
                />
              </div>
            </div>
            <button
              onClick={handleSaveExtraFee}
              data-testid="submit-fee-btn"
              className="btn-terra w-full mt-5"
            >
              Add Fee to Invoice
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
